import React from 'react'
import { BORDERRADIUS as borderRadius } from "@fedsim/constants"
import {
  Stack,
  Box,
  Card,
  Divider,
  CardMedia,
  CardContent
} from '@mui/material'
import { NamePlate, SpacedDivider } from '../typography'
import { darkenHex } from '@fedsim/helpers'
import { SPACING } from '@fedsim/constants'
import { BrandStack } from './items/stacks'
import { ShowBlock } from './items/show'
import {
  ChampionshipStack,
  SegmentStack,
  FactionStack,
  DraftStack,
  ReignStack,
  ProductionStack,
  WrestlerStack,
  FeudStack,
  AppearancesStack
} from './items/stacks'

const vacantImageUrl = '//pbs.twimg.com/profile_images/476163865571389441/P1oMUt-r_400x400.png';

const sx = { backgroundColor: '#000', color: '#fff', height: 115, width: '100%' }

export function GenericCard({
  backgroundColor = '#000',
  children = null,
  className = '',
  color = '#fff',
  image = false,
  name = '',
  Content = false
}) {
  const style = { color, backgroundColor }
  const darkenedHex = darkenHex(backgroundColor, 20)
  const cardStyle = {
    borderRadius,
    width: '100%',
    userSelect: 'none',
    overflow: 'hidden',
    flexGrow: 1
  }
  return (
    <Card
      sx={{
        boxShadow: 'none',
        ...style,
        ...cardStyle
      }}
      className={className}
    >
      {name && (
        <Box sx={{ px: 0, py: .5 }}>
          <NamePlate noWrap sx={{ color }} name={name} />
        </Box>
      )}
      {image ? (
        <>
          <CardMedia
            component='img'
            draggable='false'
            alt={name?.toString() || 'Vacant'}
            image={image}
            sx={{
              ...sx,
              objectFit: 'contain',
              boxSizing: 'border-box',
              border: 'inset 0px 6px transparent',
              color,
              backgroundColor: darkenedHex
            }}
          />
        </>
      ) : null}
      <CardContent
        sx={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          padding: 0,
        }}
      >
        {Content ? (
          <Box sx={{ mt: 0.5, p: SPACING / 3, flexGrow: 1 }}>
            <Content />
          </Box>
        ) : null}
      </CardContent>
      {children && <Box mb={SPACING} className="children">
        {children}
      </Box>}
    </Card>
  )
}

const StackWrapper = ({ children = null, sx = {} }) => (
  <Stack sx={sx} spacing={0.1}>
    {children}
  </Stack>
)


export function ChampionshipCard(props) {
  const Content = () => (
    <StackWrapper>
      <ChampionshipStack {...props} />
    </StackWrapper>
  )

  return (
    <GenericCard {...props} Content={Content}>
      {props?.children}
    </GenericCard>
  )
}

export function DraftCard(props) {
  const {
    pick = -1,
    children = null,
    brand = { name: 'Vacant', image: false },
    wrestler = { name: 'Vacant', image: false }
  } = props
  const image = props?.image || wrestler?.image || brand?.image || ''
  const name = `#${pick} - ${wrestler.name} to ${brand.name}`
  const Content = () => (
    <StackWrapper>
      <DraftStack {...props} />
    </StackWrapper>
  )
  return (
    <GenericCard {...props} image={image} Content={Content} name={name}>
      {children}
    </GenericCard>
  )
}

export function WrestlerCard(props) {
  const {
    // wins = 0,
    // losses = 0,
    // streak = 0,
    // draws = 0,
    backgroundColor = null,
    color = null,
    brands = []
  } = props
  const style = {
    color: color || brands?.[0]?.color,
    backgroundColor: backgroundColor || brands?.[0]?.backgroundColor
  }
  const image =
    props?.image ||
    vacantImageUrl
  const Content = () => (
    <Stack spacing={0.1} sx={{ color, textAlign: 'center' }}>
      <WrestlerStack {...props} />
    </Stack>
  )
  return (
    <GenericCard {...props} {...style} Content={Content} image={image}>
      {props?.children}
    </GenericCard>
  )
}

export function AppearanceCard(props) {
  return (
    <WrestlerCard {...props?.wrestler} />
  )
}

export function BrandCard(props) {
  const Content = () => (
    <Stack spacing={0.1}>
      <BrandStack {...props} />
    </Stack>
  )
  return (
    <GenericCard {...props} Content={Content}>
      {props?.children}
    </GenericCard>
  )
}

export function ShowCard(props) {
  const Content = () => (
    <Stack spacing={0.1}>
      <ShowBlock {...props} />
    </Stack>
  )
  return (
    <GenericCard {...props} Content={Content}>
      {props?.children}
    </GenericCard>
  )
}

export function ProductionCard(props) {
  const Content = () => (
    <Stack spacing={0.1}>
      <ProductionStack {...props} />
    </Stack>
  )
  return (
    <GenericCard {...props} Content={Content}>
      {props?.children}
    </GenericCard>
  )
}

export function SegmentCard(props) {
  const Content = () => (
    <>
      <Stack spacing={0.1}>
        <AppearancesStack {...props} />
        <SpacedDivider size='small' />
        <SegmentStack {...props} />
      </Stack>
    </>
  )
  return (
    <GenericCard {...props} Content={Content}>
      {props?.children}
    </GenericCard>
  )
}

export function FactionCard(props) {
  const Content = () => (
    <Stack spacing={0.1}>
      <FactionStack {...props} />
    </Stack>
  )
  return (
    <GenericCard {...props} Content={Content}>
      {props?.children}
    </GenericCard>
  )
}

export function FeudCard(props) {
  const Content = () => (
    <Stack spacing={0.1}>
      <FeudStack {...props} />
    </Stack>
  )
  return (
    <GenericCard {...props} Content={Content}>
      {props?.children}
    </GenericCard>
  )
}

export function ReignCard(props) {
  const image =
    props?.image || props?.championship?.image || props?.wrestlers?.[0]?.image

  const Content = () => (
    <Stack spacing={0.1}>
      <ReignStack {...props} />
    </Stack>
  )
  return (
    <GenericCard {...props} image={image} Content={Content}>
      {props?.children}
    </GenericCard>
  )
}

export const Cards = {
  APPEARANCE: AppearanceCard,
  BRAND: BrandCard,
  CHAMPIONSHIP: ChampionshipCard,
  DRAFT: DraftCard,
  DEFAULT: GenericCard,
  FACTION: FactionCard,
  REIGN: ReignCard,
  FEUD: FeudCard,
  PRODUCTION: ProductionCard,
  SEGMENT: SegmentCard,
  SHOW: ShowCard,
  WRESTLER: WrestlerCard
}

export const getCardComponent = (key = Cards.DEFAULT) => {
  return Cards[key.toUpperCase()]
}

export default Cards
